import "../groups/GroupCardShared.scss";

import clsx from "clsx";
import { format } from "date-fns";
import type { FC, RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { getGroupIdFromAccessId, isGroupAccessId } from "../../../../shared/access-id";
import { SectionCapability } from "../../../../shared/capabilities";
import { DueDateButton } from "../DueDateButton";
import { PageLockedChip } from "../PageLockedChip";
import { TemplatePageDisabledBanner } from "../banners/TemplatePageDisabledBanner";
import { isSubmissionLate } from "../helpers";

import { PageMaxPoints } from "./PageMaxPoints";

import { AssessmentStatus } from "api/api-data-types";
import {
  FOLLOWING_QUERY_PARAM,
  INDEX_NOT_FOUND,
  SUBMISSION_ID_QUERY_PARAM,
} from "components/constants";
import { AssessmentBanner } from "components/materials/page/banners/AssessmentBanner";
import { GroupConfigCard } from "components/materials/page/groups/GroupConfigCard";
import { PageGroupInfoCard } from "components/materials/page/groups/PageGroupInfoCard";
import { StudentSelfSelectCard } from "components/materials/page/groups/StudentSelfSelectCard";
import { PageOverviewInteractionMode } from "components/materials/page/interaction-mode/PageOverviewInteractionMode";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { InteractionMode, PageGroupUserType, SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities, useSectionUserIdsWithSectionCapability } from "store/capability-hooks";
import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  selectCurrentSectionId,
  selectCurrentSectionIsTemplate,
  selectCurrentSectionUserId,
  selectCurrentUserIsEnabledForSection,
  selectIsEditModeEnabled,
  selectIsGroupConfigShown,
  selectNestedPageOutcomes,
  selectPageGroupIdForCurrentUserByPageId,
  selectPageGroupIndexByPageGroupId,
  selectReadyPageGroupIdForCurrentUserByPageId,
  selectSelectedPageTabVariant,
} from "store/selectors";
import { selectAssessmentsByPageId } from "store/selectors/assessment";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { viewStateActions } from "store/slices/view";
import { useHasItemsSelector, useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { changePageInteractionMode, usePageActions } from "utils/page";
import {
  GROUP_INTERACTION_MODES,
  isGroupInteractionMode,
  isGroupPage,
  isPageDisabled,
  isSharedClassPage,
} from "utils/page-utils";
import { FeatureStatus, usePresentation } from "utils/presentation";
import { Link, useNavigate } from "utils/routing";
import { pageUrl } from "utils/urls";

type PageOverviewHeaderProps = {
  pageId: string;
  className?: string;
  isStaticContent?: boolean;
  readOnly?: boolean;
  selectedAccessId?: string | null;
  isProjectorView?: boolean;
};

const getPointsConfigTooltip = (
  sharedClassPage: boolean,
  canAuthorSection: boolean,
  hasAssessments: boolean,
  hasPageOutcomes: boolean,
) => {
  if (sharedClassPage) {
    return t("tooltip.points_config_shared_class");
  }

  if (canAuthorSection && hasAssessments) {
    return t("tooltip.points_config_has_assessments");
  }

  if (canAuthorSection && !hasPageOutcomes) {
    return t("page_overview_header.disabled_info");
  }

  return t("tooltip.assessment_points");
};

export const PageOverviewHeader: FC<PageOverviewHeaderProps> = ({
  pageId,
  className,
  isStaticContent,
  readOnly = false,
  selectedAccessId,
  isProjectorView = false,
}) => {
  const dispatch = useAppDispatch();
  const { savePage } = usePageActions();
  const { canAuthorSection, canParticipateInSection } = useCapabilities();
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    [
      "assessments_published_at",
      "due_at",
      "interaction_mode",
      "title",
      "id",
      "section_id",
      "released_at",
      "locked_at",
      "has_linked_submissions",
      "max_points",
      "submissions_last_collected_at",
    ],
  );
  const isCurrentPageDisabled = isPageDisabled(page);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isEditing = isEditModeEnabled && !isCurrentPageDisabled;
  const isAssessing = useIsAssessing();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isGroupConfigShown = useAppSelector(selectIsGroupConfigShown);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const sharedClassPage = isSharedClassPage(page);
  const canShowToast = useRef(true);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const currentUserEnabled = useAppSelector(selectCurrentUserIsEnabledForSection);
  const assessmentStatusInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, pageId));
  const hasSubmissions = [AssessmentStatus.ASSESSABLE, AssessmentStatus.ASSESSED].includes(
    assessmentStatusInfo?.status,
  );
  const canHaveSubmission = canParticipateInSection && currentUserEnabled;
  const navigate = useNavigate();
  const canAuthorSectionSectionUserIds = useSectionUserIdsWithSectionCapability(
    SectionCapability.CAN_AUTHOR_SECTION,
  );
  const canAuthorSectionSectionUserIdsKey = canAuthorSectionSectionUserIds.join(",");
  const showLockedChip = !!page?.locked_at;

  const hasPageOutcomes = useHasItemsSelector((s) => selectNestedPageOutcomes(s, pageId));
  const hasAssessments = useHasItemsSelector((s) => selectAssessmentsByPageId(s, pageId));
  const userPageGroupId = useAppSelector((s) => selectPageGroupIdForCurrentUserByPageId(s, pageId));
  const readyPageGroupId = useAppSelector((s) =>
    selectReadyPageGroupIdForCurrentUserByPageId(s, pageId),
  );
  const accessPageGroupId =
    selectedAccessId && isGroupAccessId(selectedAccessId)
      ? getGroupIdFromAccessId(selectedAccessId)
      : null;
  const displayedPageGroupId = canAuthorSection ? accessPageGroupId : readyPageGroupId;
  const pageGroupIndex = useAppSelector((s) =>
    selectPageGroupIndexByPageGroupId(s, pageId, displayedPageGroupId),
  );

  const [showPageGroupInfoCard, setShowPageGroupInfoCard] = useState(true);
  const interactionModeRef = useRef<HTMLButtonElement>(null);
  const pageGroupInfoCloseButtonRef = useRef<HTMLButtonElement>(null);
  const groupConfigCloseButtonRef = useRef<HTMLButtonElement>(null);

  const [dialogDescription, setDialogDescription] = useState("");
  const [dialogSubmissionId, setDialogSubmissionId] = useState("");

  const { value: dialogIsShown, setTrue: showDialog, setFalse: hideDialog } = useBoolean(false);
  const { isFollowing, presentedPageId, featureStatus } = usePresentation();

  const shouldRemainFollowing =
    isFollowing && presentedPageId === pageId && featureStatus === FeatureStatus.NONE;
  const dialogURL =
    sectionId && dialogSubmissionId
      ? pageUrl(sectionId, pageId, {
          additionalParams: {
            [SUBMISSION_ID_QUERY_PARAM]: dialogSubmissionId,
            ...(shouldRemainFollowing ? { [FOLLOWING_QUERY_PARAM]: "active" } : {}),
          },
        })
      : "";

  const isShowingStudentSelfSelectCard = Boolean(
    canParticipateInSection &&
      page?.interaction_mode === InteractionMode.SELF_ASSIGN &&
      pageGroupIndex === INDEX_NOT_FOUND,
  );

  const canSeePageGroupInfoCard = Boolean(
    ((canParticipateInSection && isGroupPage(page)) ||
      (canAuthorSection &&
        isGroupPage(page) &&
        (!isAssessing || (isAssessing && !!page?.assessments_published_at)))) &&
      pageGroupIndex !== INDEX_NOT_FOUND,
  );

  const isShowingPageGroupInfoCard =
    showPageGroupInfoCard && canSeePageGroupInfoCard && !isGroupConfigShown;

  const isShowingGroupConfigurationCard = isGroupConfigShown && !isShowingPageGroupInfoCard;

  const isShowingCurrentStudentNotInGroupBanner = Boolean(
    canParticipateInSection &&
      page?.interaction_mode === InteractionMode.ASSIGN_STUDENTS &&
      pageGroupIndex === INDEX_NOT_FOUND,
  );

  const canConfigurePoints = Boolean(
    hasPageOutcomes && canAuthorSection && !sharedClassPage && !hasAssessments,
  );
  const isPointsReadOnly = readOnly || !canAuthorSection || !isEditing;

  useEffect(() => {
    // Local UI state (showPageGroupInfoCard) resets via `key` on the callers when
    // pageId / selectedAccessId change. This only clears the shared group-config flag.
    dispatch(viewStateActions.setShowGroupConfig(false));
  }, [selectedAccessId, pageId, dispatch]);

  useEffect(() => {
    return addBroadcastListener(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, (message) => {
      // Notify students when they've submitted a late submission or when the instructor has collected their submission,
      // or when they have resubmitted before the due date.
      // Determine if we should show a modal that a submission was created.
      if (
        message.model === "submissions" &&
        message.action === "create" &&
        canShowToast.current &&
        canHaveSubmission
      ) {
        const submissionMessage = message.data as SubmissionType;
        if (submissionMessage.page_id !== pageId) {
          // Submission is for a different page, ignore it.
          return;
        }

        // If it's for a student we only want to show the modal if it's their submission.
        if (submissionMessage.section_user_id) {
          if (submissionMessage.section_user_id !== sectionUserId) {
            return;
          }
        } else if (submissionMessage.page_group_id !== userPageGroupId) {
          return;
        }

        // Don't allow toasting again in 5 seconds.
        canShowToast.current = false;

        if (tabVariant !== "my_draft") {
          toastSuccessMessage(t("success.toasts.submission_success_one"));
        } else {
          const submittedBySectionUserId = submissionMessage.submitted_by_id;
          const isSubmittedBySystem = !submittedBySectionUserId;
          const isSubmittedByInstructor =
            !!submittedBySectionUserId &&
            canAuthorSectionSectionUserIds.includes(submittedBySectionUserId);

          // Determine if submission was created after due date (more than 1 minute after)
          const isLateSubmission = isSubmissionLate(submissionMessage, page?.due_at);

          const submissionCreatedAt = new Date(submissionMessage.created_at);
          const dueDate = page?.due_at ? new Date(page.due_at) : null;
          const isBeforeDueDate = dueDate && submissionCreatedAt < dueDate;
          const dateFormat = "MMM d, HH:mm";
          const formattedDueDate = dueDate ? format(dueDate, dateFormat) : "";
          const formattedCreatedAt = format(submissionCreatedAt, dateFormat);

          setDialogSubmissionId(submissionMessage.id);

          if (isSubmittedBySystem) {
            // Submission was collected by Forum at the due date
            setDialogDescription(
              t("page_overview_header.submission_created_at_due_date", {
                due_date: formattedDueDate,
              }),
            );
          } else if (isSubmittedByInstructor) {
            if (isBeforeDueDate) {
              // Instructor collected submissions early
              setDialogDescription(
                t("page_overview_header.submission_created_before_due_date", {
                  created_at: formattedCreatedAt,
                }),
              );
            } else {
              // Display message of submission collected without noting a future due date
              setDialogDescription(
                t("page_overview_header.submission_created_no_due_date", {
                  created_at: formattedCreatedAt,
                }),
              );
            }
          } else if (isLateSubmission) {
            // Student submitted late
            setDialogDescription(
              t("page_overview_header.submission_created_user_late", {
                created_at: formattedCreatedAt,
              }),
            );
          } else {
            // Student resubmitted before the due date
            setDialogDescription(
              t("page_overview_header.submission_created_user", {
                created_at: formattedCreatedAt,
              }),
            );
          }

          showDialog();
        }

        // Allow toasting again in 5 seconds.
        setTimeout(() => {
          canShowToast.current = true;
        }, 5000);
      }

      // Refresh page access data when the current student is added to a page group.
      // This covers the case where an instructor assigns a student to a group (vs. self-assign),
      // and the student's page access data should be re-fetched under the new group principal.
      else if (
        message.model === "page_group_users" &&
        (message.action === "create" || message.action === "update") &&
        sectionUserId
      ) {
        const pageGroupUser = message.data as PageGroupUserType;
        const isForCurrentUser = pageGroupUser.section_user_id === sectionUserId;
        const pageGroup = pageGroupSelectors.selectById(
          store.getState(),
          pageGroupUser.page_group_id,
        );
        const isForThisPage = pageGroup?.page_id === pageId;
        if (isForCurrentUser && isForThisPage) {
          storeApi.page_access_data.list({ page_id: pageId }, { skipCache: true, skipToast: true });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageId,
    sectionUserId,
    canHaveSubmission,
    userPageGroupId,
    page?.due_at,
    page?.submissions_last_collected_at,
    tabVariant,
    dispatch,
    navigate,
    canAuthorSectionSectionUserIdsKey,
  ]);

  useEffect(() => {
    if (isGroupInteractionMode(page?.interaction_mode)) {
      storeApi.page_group_users.list({ page_group__page_id: pageId });
      storeApi.page_groups.list({ page_id: pageId });
    }
  }, [page?.interaction_mode, pageId, sectionId]);

  useEffect(() => {
    dispatch(viewStateActions.setShowGroupConfig(false));
  }, [isAssessing, dispatch]);

  // Refresh page group data when window regains focus
  // This is a temporary fix for issues where group distribution looks different for different users, possibly due to
  // issues with pubsub. If it's not due to pubsub, this fix will likely do nothing. Wait and re-evaluate.
  // TODO: Store: If we have versioning in websocket messages and a replay feature,
  // we should replay missed messages instead.
  useEffect(() => {
    if (
      page?.interaction_mode === InteractionMode.INDIVIDUAL ||
      page?.interaction_mode === InteractionMode.SHARED_CLASS
    ) {
      return;
    }
    const fetchPageGroupData = () => {
      return Promise.all([
        storeApi.page_group_users.list(
          { page_group__page_id: pageId },
          { skipCache: true, skipToast: true },
        ),
        storeApi.page_groups.list({ page_id: pageId }, { skipCache: true, skipToast: true }),
      ]);
    };

    window.addEventListener("focus", fetchPageGroupData);
    return () => {
      window.removeEventListener("focus", fetchPageGroupData);
    };
  }, [page?.interaction_mode, pageId, sectionId]);

  if (!page) {
    return null;
  }

  const onChangeInteractionMode = (interactionMode: InteractionMode) => {
    const isNowGrouped = isGroupInteractionMode(interactionMode);

    if (interactionMode === page.interaction_mode) {
      if (isNowGrouped) {
        dispatch(viewStateActions.setShowGroupConfig(!isGroupConfigShown));
        if (!isGroupConfigShown) {
          setTimeout(() => groupConfigCloseButtonRef.current?.focus(), 0);
        }
      }

      return;
    }

    if (!isInTemplateSection) {
      const showConfigGroup = isNowGrouped ? !isGroupConfigShown : false;
      dispatch(viewStateActions.setShowGroupConfig(showConfigGroup));
      if (showConfigGroup) {
        setTimeout(() => groupConfigCloseButtonRef.current?.focus(), 0);
      }
    }

    changePageInteractionMode({
      page,
      newInteractionMode: interactionMode,
      isInTemplateSection,
    });
  };

  const showPointsMenu =
    (canAuthorSection && isEditing && !isStaticContent && !readOnly) || page?.max_points > 0;

  const canOpenPageGroupInfoCard =
    GROUP_INTERACTION_MODES.includes(page.interaction_mode) &&
    pageGroupIndex !== INDEX_NOT_FOUND &&
    !isShowingCurrentStudentNotInGroupBanner &&
    canSeePageGroupInfoCard;

  const onGroupCardToggle = canOpenPageGroupInfoCard
    ? () => {
        setShowPageGroupInfoCard(!showPageGroupInfoCard);
        if (!showPageGroupInfoCard) {
          setTimeout(() => pageGroupInfoCloseButtonRef.current?.focus(), 0);
        }
      }
    : !readOnly && canAuthorSection
      ? () => onChangeInteractionMode(page.interaction_mode)
      : undefined;

  return (
    <div className={twMerge("flex w-full flex-col items-center px-4 md:px-10", className)}>
      <div
        className={clsx(
          "flex w-full flex-col",
          !readOnly && "max-w-[--page-default-max-width] gap-5",
        )}
      >
        <div
          className={clsx(
            "page-metadata-container flex w-full flex-wrap items-center gap-2 @container/page-metadata",
            isProjectorView ? "justify-end" : "",
          )}
        >
          {!isStaticContent ? (
            <PageOverviewInteractionMode
              canChangeMode={isEditing && !readOnly}
              hasSubmissions={hasSubmissions}
              pageGroupIndex={pageGroupIndex}
              pageId={pageId}
              ref={interactionModeRef}
              onGroupCardToggle={onGroupCardToggle}
              onModeChange={readOnly ? undefined : onChangeInteractionMode}
            />
          ) : null}

          {showPointsMenu ? (
            <PageMaxPoints
              disabled={!canConfigurePoints}
              pageId={pageId}
              readOnly={isPointsReadOnly}
              tooltipText={getPointsConfigTooltip(
                sharedClassPage,
                canAuthorSection,
                hasAssessments,
                hasPageOutcomes,
              )}
              isPageOverviewHeader
              onSave={(points) => savePage({ id: pageId, max_points: points })}
            />
          ) : null}

          {!isInTemplateSection && <DueDateButton pageId={pageId} readOnly={readOnly} />}

          {showLockedChip && <PageLockedChip canAuthorSection={canAuthorSection} />}
        </div>

        {isShowingPageGroupInfoCard && (
          <PageGroupInfoCard
            isInstructorView={canAuthorSection}
            pageGroupId={displayedPageGroupId}
            pageGroupIndex={pageGroupIndex}
            isActive
            shouldFilterForReadiedUsers
          >
            <SquareButton
              kind="accent"
              ref={pageGroupInfoCloseButtonRef}
              size="xs"
              onClick={() => {
                setShowPageGroupInfoCard(false);
                interactionModeRef.current?.focus();
              }}
            >
              {t("common.close")}
            </SquareButton>
          </PageGroupInfoCard>
        )}

        {isShowingGroupConfigurationCard && (
          <GroupConfigCard
            closeButtonRef={groupConfigCloseButtonRef}
            pageId={pageId}
            triggerRef={interactionModeRef as RefObject<HTMLButtonElement>}
          />
        )}

        {isShowingStudentSelfSelectCard && <StudentSelfSelectCard pageId={pageId} />}

        {!isProjectorView && <AssessmentBanner className={className} pageId={pageId} />}
      </div>

      {canAuthorSection && isInTemplateSection && isCurrentPageDisabled && isEditModeEnabled && (
        <div className="mt-4 w-full max-w-[--page-default-max-width]">
          <TemplatePageDisabledBanner />
        </div>
      )}

      <Dialog
        description={dialogDescription}
        dismissible={false}
        open={dialogIsShown}
        title={t("page_overview_header.submission_created_title")}
        onClose={hideDialog}
      >
        <div className="flex w-full justify-end pt-4">
          <SquareButton
            as={Link}
            kind="accent"
            size="m"
            to={dialogURL}
            autoFocus
            onClick={() => {
              hideDialog();
              dispatch(viewStateActions.setShouldResetPageTab(true));
            }}
          >
            {t("page_overview_header.view_submission")}
          </SquareButton>
        </div>
      </Dialog>
    </div>
  );
};
